import { SupportLayout } from "@/components/support-layout";
import { HelpCircle } from "lucide-react";

export const FAQ = () => {
  return (
    <SupportLayout>
      <h1 className="text-3xl font-bold mb-2">Frequently Asked Questions</h1>

      <div className="text-sm text-muted-foreground mb-8">
        <p>Common questions about trading on Nordex</p>
      </div>

      <section className="mb-12">
        <div className="space-y-4">
          {/* Getting Started */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              How do I get started on Nordex?
            </h3>
            <p className="text-sm text-muted-foreground">
              Connect your Solana wallet or sign in with email/social login via
              Privy. Once connected, deposit USDC to your trading account via
              the "Deposit" button. You can then start trading perpetual futures
              with leverage up to 100x.
            </p>
          </div>

          {/* Wallets */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              Which wallets are supported?
            </h3>
            <p className="text-sm text-muted-foreground mb-2">
              Nordex supports all major Solana wallets including:
            </p>
            <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
              <li>Phantom</li>
              <li>Solflare</li>
              <li>Backpack</li>
              <li>Any Solana wallet adapter compatible wallet</li>
              <li>
                Email/Social login (powered by Privy) - no wallet needed
              </li>
            </ul>
          </div>

          {/* Deposits */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              How do I deposit funds?
            </h3>
            <p className="text-sm text-muted-foreground">
              Click the "Deposit" button in the top right corner of the
              platform. Enter the amount of USDC you want to deposit and confirm
              the transaction in your wallet. Deposits are processed on-chain
              via Solana and typically complete within seconds.
            </p>
          </div>

          {/* Withdrawals */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              How do I withdraw funds?
            </h3>
            <p className="text-sm text-muted-foreground">
              Navigate to your Account page by clicking "Account" in the mobile
              menu or accessing it from the header. Go to the Withdrawals
              section, enter the amount you want to withdraw, and confirm the
              transaction. Withdrawals are processed on-chain and typically
              complete within a few minutes. Note: You must close all open
              positions before withdrawing your full balance.
            </p>
          </div>

          {/* Trading */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              What order types are available?
            </h3>
            <p className="text-sm text-muted-foreground mb-2">
              Nordex supports the following order types:
            </p>
            <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
              <li>
                <strong>Market Orders:</strong> Execute immediately at current
                market price
              </li>
              <li>
                <strong>Limit Orders:</strong> Execute only at your specified
                price or better
              </li>
              <li>
                <strong>Reduce-Only Orders:</strong> Can only reduce existing
                positions, not increase them
              </li>
            </ul>
          </div>

          {/* Leverage */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              How does leverage work?
            </h3>
            <p className="text-sm text-muted-foreground">
              Leverage allows you to control a larger position with less capital.
              For example, with 10x leverage, you can open a $10,000 position
              with just $1,000 in collateral. Nordex currently offers up to 100x
              leverage (leverage limits may vary by market and position size).
              Higher leverage amplifies both profits and losses.
            </p>
          </div>

          {/* Liquidation */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              What is liquidation and how can I avoid it?
            </h3>
            <p className="text-sm text-muted-foreground mb-2">
              Liquidation occurs when your margin health falls below the
              maintenance margin requirement. To avoid liquidation:
            </p>
            <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
              <li>Monitor your margin health bar in the Account panel</li>
              <li>Add more collateral by depositing additional USDC</li>
              <li>Reduce position sizes or leverage</li>
              <li>
                Use lower leverage for more buffer against price volatility
              </li>
            </ul>
          </div>

          {/* Fees */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              What fees does Nordex charge?
            </h3>
            <p className="text-sm text-muted-foreground">
              Nordex charges maker and taker fees on trades, plus variable funding rates on open perpetual positions every 8 hours. For current rates and detailed examples, see our <a href="/fees" className="text-primary hover:underline">Fee Structure</a> page.
            </p>
          </div>

          {/* Funding Rates */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              What are funding rates?
            </h3>
            <p className="text-sm text-muted-foreground">
              Funding rates are periodic payments exchanged between long and
              short traders to keep perpetual futures prices aligned with spot
              prices. When the funding rate is positive, long positions pay
              shorts. When negative, shorts pay longs. Funding occurs every 8
              hours on Nordex.
            </p>
          </div>

          {/* Markets */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              What markets can I trade?
            </h3>
            <p className="text-sm text-muted-foreground">
              Nordex offers perpetual futures on major cryptocurrency markets
              including BTC, ETH, SOL, and more. All markets are quoted in USDC.
              You can switch between markets using the product selector in the
              header.
            </p>
          </div>

          {/* Performance */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              How do I track my performance?
            </h3>
            <p className="text-sm text-muted-foreground">
              Access your Account page to view comprehensive account information
              including total equity, available balance, margin usage, unrealized
              P&L across all positions, realized P&L history, trade history,
              funding payment history, and withdrawal history.
            </p>
          </div>

          {/* Referrals */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              Does Nordex have a referral program?
            </h3>
            <p className="text-sm text-muted-foreground">
              Yes! Nordex offers an affiliate and referral program. You can earn
              rewards by referring new traders to the platform. Access the
              Affiliate page from the main menu to get your unique referral
              link, track referrals, and view your earnings.
            </p>
          </div>

          {/* API */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              Is there an API for automated trading?
            </h3>
            <p className="text-sm text-muted-foreground">
              Yes, Nordex provides REST and WebSocket APIs for programmatic
              trading. The API supports order placement, position management,
              account data retrieval, and real-time market data subscriptions.
              <a href="/contact" className="text-primary hover:underline">Contact us</a> for API documentation and
              authentication credentials.
            </p>
          </div>

          {/* UI Customization */}
          <div className="p-4 border border-border rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              Can I customize the trading interface?
            </h3>
            <p className="text-sm text-muted-foreground">
              Absolutely! Nordex features a flexible panel layout system. Click
              the "+" button in the header to add panels like Order Book, Market
              Trades, Market Chart, Positions, Account summary, and more. Drag
              panel tabs to rearrange, resize panels by dragging borders, and
              create multiple custom layouts. Your layouts are automatically
              saved.
            </p>
          </div>
        </div>
      </section>

    </SupportLayout>
  );
};
